import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { ChevronRight, ChevronLeft, Send, Check } from 'lucide-react';

const MASK_TYPES = [
  { id: 'hockey', emoji: '🏒' },
  { id: 'floorball', emoji: '🥅' },
];

const STYLE_MOTIFS = [
  { id: 'animal', emoji: '🐻' },
  { id: 'character', emoji: '⚔️' },
  { id: 'team-logo', emoji: '🏆' },
  { id: 'custom', emoji: '🎨' },
];

const COLOR_PRESETS = [
  { id: 'dark', colors: ['#1a1a2e', '#16213e', '#0f3460'] },
  { id: 'fire', colors: ['#e6003a', '#ff6b00', '#ffd700'] },
  { id: 'ice', colors: ['#00c8e6', '#0077b6', '#caf0f8'] },
  { id: 'forest', colors: ['#1b4332', '#2d6a4f', '#52b788'] },
  { id: 'royal', colors: ['#7b2cbf', '#c77dff', '#e0aaff'] },
  { id: 'custom', colors: ['#888', '#aaa', '#ccc'] },
];

const MaskConfigurator = () => {
  const { t } = useLanguage();
  const [step, setStep] = useState(0);
  const [maskType, setMaskType] = useState('');
  const [colorPreset, setColorPreset] = useState('');
  const [motif, setMotif] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const conf = t.configurator;

  const steps = [conf.step1, conf.step2, conf.step3, conf.step4, conf.step5];

  const canNext = () => {
    if (step === 0) return !!maskType;
    if (step === 1) return !!colorPreset;
    if (step === 2) return !!motif;
    if (step === 3) return description.trim().length > 0;
    if (step === 4) return email.trim().length > 0 && name.trim().length > 0;
    return true;
  };

  const handleSubmit = async () => {
    setSending(true);
    try {
      const id = crypto.randomUUID();
      const message = `[KONFIGURÁTOR] Typ: ${maskType}, Barvy: ${colorPreset}, Motiv: ${motif}\n\n${description}`;
      await supabase.from('contact_messages').insert({
        id,
        name,
        email,
        message,
      });

      // Fire-and-forget admin notifications (same as ContactSection)
      const { ADMIN_NOTIFICATION_EMAILS } = await import('@/lib/notifications');
      ADMIN_NOTIFICATION_EMAILS.forEach((adminEmail) => {
        const slug = adminEmail.split('@')[0].replace(/[^a-z0-9]/gi, '');
        supabase.functions.invoke('send-transactional-email', {
          body: {
            templateName: 'admin-new-contact-message',
            recipientEmail: adminEmail,
            idempotencyKey: `admin-config-${id}-${slug}`,
            templateData: { name, email, phone: '', message },
          },
        }).catch((err) => console.error(`Admin notification to ${adminEmail} failed:`, err));
      });

      setSubmitted(true);
      toast({ title: conf.successTitle, description: conf.success });
    } catch {
      toast({ title: 'Chyba', description: 'Něco se pokazilo', variant: 'destructive' });
    }
    setSending(false);
  };

  if (submitted) {
    return (
      <section className="py-20 md:py-32 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/20 text-primary mb-6">
            <Check size={40} />
          </motion.div>
          <h2 className="text-4xl md:text-6xl text-foreground tracking-wider" style={{ fontFamily: 'Bebas Neue' }}>{conf.successTitle}</h2>
          <p className="mt-4 text-muted-foreground text-lg">{conf.success}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="configurator" className="py-20 md:py-32 px-4">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-5xl md:text-7xl tracking-wider text-foreground" style={{ fontFamily: 'Bebas Neue' }}>
            {conf.title}
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">{conf.subtitle}</p>
        </motion.div>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-2 mb-10">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                i <= step ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'
              }`}>
                {i < step ? '✓' : i + 1}
              </div>
              {i < steps.length - 1 && (
                <div className={`w-8 h-0.5 transition-colors ${i < step ? 'bg-primary' : 'bg-border'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="bg-card border border-border rounded-xl p-8 min-h-[300px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <h3 className="text-2xl text-foreground mb-6" style={{ fontFamily: 'Bebas Neue' }}>{steps[step]}</h3>

              {step === 0 && (
                <div className="grid grid-cols-2 gap-4">
                  {MASK_TYPES.map(({ id, emoji }) => (
                    <button
                      key={id}
                      onClick={() => setMaskType(id)}
                      className={`p-6 rounded-lg border-2 text-center transition-all ${
                        maskType === id ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <span className="text-4xl block mb-2">{emoji}</span>
                      <span className="text-foreground font-medium">{(conf as any)[id]}</span>
                    </button>
                  ))}
                </div>
              )}

              {step === 1 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {COLOR_PRESETS.map(({ id, colors }) => (
                    <button
                      key={id}
                      onClick={() => setColorPreset(id)}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        colorPreset === id ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex gap-1 mb-2 justify-center">
                        {colors.map((c, i) => (
                          <div key={i} className="w-8 h-8 rounded-full" style={{ backgroundColor: c }} />
                        ))}
                      </div>
                      <span className="text-foreground text-sm">{(conf as any)[id]}</span>
                    </button>
                  ))}
                </div>
              )}

              {step === 2 && (
                <div className="grid grid-cols-2 gap-4">
                  {STYLE_MOTIFS.map(({ id, emoji }) => (
                    <button
                      key={id}
                      onClick={() => setMotif(id)}
                      className={`p-6 rounded-lg border-2 text-center transition-all ${
                        motif === id ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <span className="text-4xl block mb-2">{emoji}</span>
                      <span className="text-foreground font-medium">{(conf as any)[id]}</span>
                    </button>
                  ))}
                </div>
              )}

              {step === 3 && (
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={conf.descPlaceholder}
                  className="w-full h-40 rounded-lg border border-input bg-background text-foreground p-4 resize-none focus:ring-2 focus:ring-primary focus:outline-none"
                />
              )}

              {step === 4 && (
                <div className="space-y-4">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={conf.name}
                    className="w-full h-12 rounded-lg border border-input bg-background text-foreground px-4 focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={conf.email}
                    className="w-full h-12 rounded-lg border border-input bg-background text-foreground px-4 focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <button
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={step === 0}
            className="flex items-center gap-2 px-6 py-3 rounded-lg border border-border text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
          >
            <ChevronLeft size={18} /> {conf.prev}
          </button>

          {step < 4 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={!canNext()}
              className="flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-30 transition-colors font-medium"
            >
              {conf.next} <ChevronRight size={18} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canNext() || sending}
              className="flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-30 transition-colors font-medium"
            >
              <Send size={18} /> {conf.send}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default MaskConfigurator;
